import React, {Children} from 'react';
import {WithTranslation} from 'react-i18next';
import {View} from 'react-native';
import {noop} from 'lodash';
import {compact, flatten, flow, map} from 'lodash/fp';

import ListItem from '/component/base/ListItem';
import Switch from '/component/base/Switch';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {Settings} from '/constant/TestIDs';
import {withTheme} from '/provider/ThemeProvider';
import testProperties from '/util/testing/testProperties';

import {styles} from './SwitchSection.style';

const uncappedMap = map.convert({cap: false});

interface SwitchItem {
  title: string;
  onToggleSwitch: () => void;
  isEnabled: boolean;
  enableSwitch: boolean;
  isDisabled: boolean;
  isClickable: boolean;
  buttonDisabled: boolean;
  onPress: () => void;
  accessibilityLabel: string;
}

interface SwitchSectionProps extends WithTranslation {
  sectionTitle: React.ReactNode;
  items: SwitchItem[];
  getTheme: () => void;
}

const SwitchSection: React.FC<SwitchSectionProps> = ({
  sectionTitle = null,
  items = [],
  getTheme = noop,
}) => (
  <View style={styles.section}>
    {sectionTitle && (
      <Gutter>
        <View style={styles.header}>
          <Text variant="heading">{sectionTitle}</Text>
        </View>
      </Gutter>
    )}
    {flow([
      // This list of `ListItem` can't use `Gutter` because otherwise the MaterialDesign's
      // "glow" when the user touches the toggle switch would be cut-off but the Gutter component.
      uncappedMap(
        (
          {
            title,
            onToggleSwitch,
            isEnabled,
            enableSwitch,
            isDisabled,
            isClickable,
            buttonDisabled,
            onPress,
            accessibilityLabel,
          }: SwitchItem,
          index: number,
        ) => [
          index > 0 ? <View style={styles.divider} /> : null,
          <ListItem
            testID={Settings.switchSection}
            readonly
            label={
              <View style={styles.listItemLabel} accessible={true}>
                <TouchableOpacity
                  accessible={true}
                  accessibilityRole="switch"
                  {...testProperties(null, false)}
                  disabled={isClickable && buttonDisabled}
                  onPress={onPress}>
                  <View accessible={false}>
                    <Text
                      testID={Settings.switchTitle}
                      emphasis="high"
                      style={
                        !buttonDisabled
                          ? {
                              ...styles.listItemLabelText,
                              ...getTheme('competency.textColor'),
                            }
                          : {
                              ...styles.listItemLabelText,
                              ...styles.listItemTextColor,
                            }
                      }>
                      {title}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            }
            suffix={
              <View style={styles.listItemSuffix} accessible={false}>
                {enableSwitch && (
                  <Switch
                    accessibilityRole="switch"
                    testID={Settings.switchElement}
                    onToggleSwitch={onToggleSwitch}
                    isEnabled={isEnabled}
                    isDisabled={isDisabled}
                    accessibilityLabel={accessibilityLabel}
                  />
                )}
              </View>
            }
          />,
        ],
      ),
      flatten,
      compact,
      Children.toArray,
    ])(items)}
  </View>
);

export default withTheme(SwitchSection);
